// SwipeDemo.tsx
import { IconLoader2 } from '@tabler/icons-react'
import type React from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

export interface SwipeButtonProps {
  /** 初期位置 */
  initialCardLeft?: number
  /** コンテナ幅の 90 % */
  swipeThresholdPercentage?: number
  /** コンテナ幅の 60 % */
  textClearThresholdPercentage?: number
  /** swipeが完了した時に実行する関数 */
  onSwipeDone: () => void
  /** 表示するテキスト */
  text: string
  /** スワイプ途中に表示するテキスト */
  inProgressText?: string
  /** ボタンの横幅 */
  width?: string
  /** テキストサイズ */
  textSize: string
  /** ボタンの色 と スワイプ後の背景色 */
  bgColor?: string
  /** ローダーの側の色 */
  spinnerColor?: string
}

export const SwipeButton = ({
  text,
  inProgressText,
  onSwipeDone,
  initialCardLeft = 10,
  swipeThresholdPercentage = 0.9,
  textClearThresholdPercentage = 0.5,
  width = 'w-11/12',
  textSize = 'text-sm',
  bgColor = 'bg-main',
  spinnerColor = 'text-on-main',
}: SwipeButtonProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)

  /** 動的スタイル用 state */
  const [translateX, setTranslateX] = useState<number>(initialCardLeft)
  const [transition, setTransition] = useState<string>('transform 0.3s ease-out')
  const [swipeDone, setSwipeDone] = useState<boolean>(false)

  /** スワイプ計算用 ref (再レンダーさせたくない) */
  const startXRef = useRef<number>(0)
  const distXRef = useRef<number>(0)
  const isSwipingRef = useRef<boolean>(false)
  const swipeThresholdRef = useRef<number>(0)

  /** 文字の透明度 */
  const [textOpacity, setTextOpacity] = useState<number>(1)
  const [inProgressTextOpacity, setInProgressTextOpacity] = useState<number>(0)

  /** コンテナ幅が変わったら閾値を再計算 */
  useLayoutEffect(() => {
    const calcThreshold = () => {
      if (containerRef.current) {
        swipeThresholdRef.current = containerRef.current.offsetWidth * swipeThresholdPercentage
      }
    }
    calcThreshold()
    window.addEventListener('resize', calcThreshold)
    return () => window.removeEventListener('resize', calcThreshold)
  }, [swipeThresholdRef, translateX])

  /** 共通処理 */
  const startSwipe = (clientX: number) => {
    // メッセージ表示中は無効
    if (swipeDone) return
    isSwipingRef.current = true
    startXRef.current = clientX
    setTransition('none') // スワイプ中はトランジション無効
  }

  const moveSwipe = (clientX: number) => {
    if (!isSwipingRef.current) return
    distXRef.current = clientX - startXRef.current

    const newX =
      distXRef.current > 0
        ? initialCardLeft + distXRef.current // 右スワイプ
        : initialCardLeft + distXRef.current / 3 // 左へは 1/3 抵抗

    setTranslateX(newX)
  }

  const endSwipe = () => {
    if (!isSwipingRef.current) return
    isSwipingRef.current = false

    // 成功判定
    if (distXRef.current > swipeThresholdRef.current) {
      const containerWidth = containerRef.current?.offsetWidth ?? 0
      const cardWidth = cardRef.current?.offsetWidth ?? 0

      setTransition('transform 0.3s ease-in-out')
      // 右端ちょい超え
      setTranslateX(containerWidth - cardWidth - initialCardLeft + 30)

      // メッセージ表示 → 完全に画面外へ
      setTimeout(() => {
        setSwipeDone(true)
        setTranslateX(containerWidth + 50)
      }, 150)
    } else {
      // 戻す
      setTransition('transform 0.3s ease-out')
      setTranslateX(initialCardLeft)
      setTextOpacity(1)
      setInProgressTextOpacity(0)
    }
  }

  /** ハンドラ (Touch) */
  const handleTouchStart: React.TouchEventHandler<HTMLDivElement> = (e) => {
    startSwipe(e.touches[0].clientX)
  }
  const handleTouchMove: React.TouchEventHandler<HTMLDivElement> = (e) => {
    moveSwipe(e.touches[0].clientX)
    // テキストのopacityを計算
    const containerWidth = containerRef.current?.offsetWidth ?? 400
    const progress = Math.min(Math.max(translateX / (containerWidth * textClearThresholdPercentage), 0), 1)
    setTextOpacity(1 - progress)
    setInProgressTextOpacity(Math.floor(progress))
  }
  const handleTouchEnd: React.TouchEventHandler<HTMLDivElement> = () => endSwipe()

  /** ハンドラ (Mouse – PC 用) */
  const handleMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    startSwipe(e.clientX)
    // ドキュメントに move / up を張る
    const handleMouseMove = (moveEvent: MouseEvent) => moveSwipe(moveEvent.clientX)
    const handleMouseUp = () => {
      endSwipe()
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  useEffect(() => {
    if (swipeDone) {
      // ユーザー定義関数の実行
      onSwipeDone()
    }
  }, [swipeDone])

  // Spinner（ローディングスピナー）コンポーネントを追加
  const Spinner = () => (
    <IconLoader2 className={`animate-spin h-6 w-6 ${spinnerColor}`} size={24} stroke={2} aria-hidden="true" />
  )

  return (
    <div className={'flex items-center justify-center '}>
      {/* スワイプ領域 */}
      <div
        ref={containerRef}
        className={`rounded-full border-2 relative ${width} ${swipeDone ? bgColor : ''} h-[56px] shadow-lg overflow-hidden touch-pan-y`}
      >
        {/* スワイプカード */}
        <div
          ref={cardRef}
          className={`z-10 absolute top-1.5 -left-0.5 flex h-[40px] w-[40px] cursor-grab select-none items-center justify-center rounded-full ${bgColor} text-xl font-semibold text-white shadow-lg`}
          style={{
            transform: `translateX(${translateX}px)`,
            transition,
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
        />
        {/* スワイプ領域の下に存在している文字列 */}
        <div
          className={
            'absolute left-[60%] top-1/2 z-1 w-9/12 -translate-x-1/2 -translate-y-1/2 rounded-xl py-6 text-center shadow-xl'
          }
          style={{ opacity: textOpacity, transition: 'opacity 0.2s' }}
        >
          <div className={`${textSize} font-bold truncate`}>{text}</div>
        </div>
        {/* スワイプ中のテキスト */}
        {inProgressText && !swipeDone && (
          <div
            className="absolute left-[45%] top-1/2 z-1 w-72 -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white py-6 text-center shadow-xl pl-[50px]"
            style={{ opacity: inProgressTextOpacity, transition: 'opacity 0.2s' }}
          >
            <div className={`${textSize} font-bold truncate`}>{inProgressText}</div>
          </div>
        )}
        {/* スワイプ後のテキスト */}
        {swipeDone ? (
          <div className="absolute top-1/2 z-1 w-full -translate-y-1/2 rounded-xl py-6 text-center shadow-xl flex justify-center items-center">
            <Spinner />
          </div>
        ) : null}
      </div>
    </div>
  )
}
